<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="filterRequests"
            item-key="id"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            fixed-header
            :page.sync="tablePage"
            :items-per-page.sync="itemsPerPage"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Purchases Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="align-center py-4 mx-0"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="6" class="d-flex align-center">
                        <h3 class="text-h6 font-weight-bold text-primary">
                            WITH PO
                        </h3>
                    </v-col>
                    <v-col
                        cols="6"
                        class="d-flex justify-end align-center pr-sm-8 pr-md-2 pr-lg-3 pr-xl-3"
                    >
                        <v-btn
                            rounded
                            color="primary"
                            @click="uploadInvoices"
                            :loading="uploadLoading"
                            v-if="editPurchasingInvoicePermission"
                        >
                            <v-icon left>mdi-upload</v-icon>
                            Upload Invoices
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.requestNumber`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="idToFilterDraft"
                    @input="scheduleFilterSync"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.type`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="typeToFilterDraft"
                    @input="scheduleFilterSync"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="projectToFilterDraft"
                    @input="scheduleFilterSync"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.items`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="itemsToFilterDraft"
                    @input="scheduleFilterSync"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.purchaseOrder.orderNumber`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="orderToFilterDraft"
                    @input="scheduleFilterSync"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.supplier`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="supplierToFilterDraft"
                    @input="scheduleFilterSync"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoices`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="invoicesToFilterDraft"
                    @input="scheduleFilterSync"
                    placeholder="Search by consecutive, filename or order number"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoiceIssueDate`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="issueDateToFilterDraft"
                    @input="scheduleFilterSync"
                    placeholder="Search by issue date"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoiceDueDate`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="dueDateToFilterDraft"
                    @input="scheduleFilterSync"
                    placeholder="Search by due date"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.invoiceSap`]="{ header }">
                <v-select
                    :label="header.text"
                    v-model="sapToFilter"
                    :items="[
                        { text: 'None', value: null },
                        { text: 'Checked', value: true },
                        { text: 'Unchecked', value: false },
                    ]"
                    item-text="text"
                    item-value="value"
                    @change="scheduleFilterSync"
                    dense
                    class="pt-2"
                    hide-details
                />
            </template>
            <template v-slot:[`header.invoiceAccountant`]="{ header }">
                <v-select
                    :label="header.text"
                    v-model="accountantToFilter"
                    :items="[
                        { text: 'None', value: null },
                        { text: 'Checked', value: true },
                        { text: 'Unchecked', value: false },
                    ]"
                    item-text="text"
                    item-value="value"
                    @change="scheduleFilterSync"
                    dense
                    class="pt-2"
                    hide-details
                />
            </template>

            <!--ITEMS-->
            <template v-slot:[`item.requestNumber`]="{ item }">
                <p class="my-0">
                    {{ item.requestNumber }}
                </p>
            </template>
            <template v-slot:[`item.id`]="{ item }">
                <p class="my-0">
                    {{ item.id }}
                </p>
            </template>

            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="my-0">
                    <div>
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ item.createdBy.name }}
                                </p>
                            </template>
                            <span>Creator</span>
                        </v-tooltip>
                    </div>
                    <div v-if="item.projectManager">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ item.projectManager.name }}
                                </p>
                            </template>
                            <span>Project Manager</span>
                        </v-tooltip>
                    </div>
                    <div v-if="item.buyer">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ getUser(item.buyer) }}
                                </p>
                            </template>
                            <span>Buyer</span>
                        </v-tooltip>
                    </div>
                </div>
            </template>
            <template v-slot:[`item.items`]="{ item: purchase }">
                <div>
                    <p class="my-0 font-weight-bold">
                        {{ purchase.totalItems }} items
                    </p>
                    <p class="my-0 text-caption">
                        {{ purchase.items.map(i => i.description).join(', ') }}
                    </p>
                </div>
            </template>

            <template v-slot:[`item.quantity`]="{ item: purchase }">
                <div class="d-flex align-center justify-center">
                    <div
                        class="quantity-container d-flex align-center"
                        :style="{
                            backgroundColor:
                                getPurchaseQuantityStatusColor(purchase) ===
                                'green'
                                    ? '#4caf50'
                                    : getPurchaseQuantityStatusColor(
                                          purchase
                                      ) === 'orange'
                                    ? '#ff9800'
                                    : '#f44336',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                        }"
                    >
                        {{ purchase.totalReceived }} /
                        {{ purchase.totalQuantity }}
                    </div>
                </div>
            </template>
            <template
                v-slot:[`item.purchaseOrder.orderNumber`]="{ item: purchase }"
            >
                <div>
                    <p class="my-0">
                        {{
                            purchase.orderNumbers.length > 0
                                ? purchase.orderNumbers.join(', ')
                                : "doesn't have orderNumbers"
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.supplier`]="{ item: purchase }">
                <div>
                    <p class="my-0">
                        {{
                            purchase.suppliers.length > 0
                                ? purchase.suppliers.join(', ')
                                : 'No suppliers'
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.invoices`]="{ item: purchase }">
                <div
                    v-if="
                        getDisplayedInvoices(purchase) &&
                            getDisplayedInvoices(purchase).length > 0
                    "
                >
                    <div
                        v-for="(invoice, index) in getDisplayedInvoices(
                            purchase
                        )"
                        :key="index"
                        class="d-flex align-center justify-start invoice-item-row"
                        :style="{
                            color: invoiceMarked(invoice)
                                ? '#f44336'
                                : 'inherit',
                        }"
                    >
                        <div
                            class="invoice-filename-container"
                            :style="{
                                minWidth: '140px',
                                maxWidth: '140px',
                            }"
                        >
                            <p
                                v-if="invoice.fileName || invoice.consecutive"
                                class="my-0 invoice-filename"
                                :style="{
                                    'font-size': '11px',
                                    color: invoiceNameColor(invoice),
                                    cursor: 'pointer',
                                    'text-decoration': 'underline',
                                }"
                                @click="openInvoice(invoice, purchase)"
                            >
                                {{
                                    invoice.consecutive
                                        ? invoice.consecutive
                                        : invoice.fileName
                                }}
                            </p>
                            <p
                                v-else
                                class="my-0"
                                :style="{
                                    color: invoiceMarked(invoice)
                                        ? '#f44336'
                                        : 'inherit',
                                }"
                            >
                                doesn't have files
                            </p>
                        </div>
                        <v-btn
                            v-if="canOpenInvoiceForm"
                            x-small
                            icon
                            :color="
                                invoiceMarked(invoice) ? 'error' : 'primary'
                            "
                            class="mx-1"
                            @click.stop="openEditInvoice(invoice, purchase)"
                        >
                            <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="deleteAccountingInvoicePermission"
                            x-small
                            icon
                            color="error"
                            class="mx-1"
                            @click.stop="deleteInvoice(invoice, purchase)"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">doesn't have invoices</p>
                </div>
            </template>
            <template v-slot:[`item.invoiceIssueDate`]="{ item: purchase }">
                <div
                    v-if="
                        getDisplayedInvoices(purchase) &&
                            getDisplayedInvoices(purchase).length > 0
                    "
                >
                    <div
                        v-for="(invoice, index) in getDisplayedInvoices(
                            purchase
                        )"
                        :key="index"
                        class="invoice-item-row d-flex align-center justify-center"
                    >
                        <p
                            class="my-0 invoice-date"
                            :style="{
                                color: invoiceMarked(invoice)
                                    ? '#f44336'
                                    : 'inherit',
                            }"
                        >
                            {{
                                invoice.invoicesDate
                                    ? invoice.invoicesDate.split('T')[0]
                                    : ' - '
                            }}
                        </p>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">-</p>
                </div>
            </template>
            <template v-slot:[`item.invoiceDueDate`]="{ item: purchase }">
                <div
                    v-if="
                        getDisplayedInvoices(purchase) &&
                            getDisplayedInvoices(purchase).length > 0
                    "
                >
                    <div
                        v-for="(invoice, index) in getDisplayedInvoices(
                            purchase
                        )"
                        :key="index"
                        class="invoice-item-row d-flex align-center justify-center"
                    >
                        <p
                            class="my-0 invoice-date"
                            :style="{
                                color: invoiceMarked(invoice)
                                    ? '#f44336'
                                    : 'inherit',
                            }"
                        >
                            {{ invoice.dueDate || ' - ' }}
                        </p>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">-</p>
                </div>
            </template>
            <template v-slot:[`item.invoiceSap`]="{ item: purchase }">
                <div
                    v-if="
                        getDisplayedInvoices(purchase) &&
                            getDisplayedInvoices(purchase).length > 0
                    "
                >
                    <div
                        v-for="(invoice, index) in getDisplayedInvoices(
                            purchase
                        )"
                        :key="index"
                        class="invoice-item-row d-flex align-center justify-center"
                    >
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <div
                                    @click.stop="
                                        onInvoiceCheckboxClick(
                                            invoice,
                                            purchase
                                        )
                                    "
                                    :style="{
                                        cursor: invoice.rejected
                                            ? 'default'
                                            : 'pointer',
                                    }"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-checkbox
                                        :value="
                                            getInvoiceSapStatus(
                                                invoice,
                                                purchase
                                            )
                                        "
                                        :input-value="
                                            getInvoiceSapStatus(
                                                invoice,
                                                purchase
                                            )
                                        "
                                        disabled
                                        dense
                                        hide-details
                                        class="my-0"
                                        :color="
                                            invoice.rejected
                                                ? 'grey'
                                                : invoiceMarked(invoice)
                                                ? 'error'
                                                : 'success'
                                        "
                                    />
                                </div>
                            </template>
                            <span>{{
                                invoice.rejected
                                    ? 'Rejected invoice'
                                    : 'Click to relate invoice'
                            }}</span>
                        </v-tooltip>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">-</p>
                </div>
            </template>
            <template v-slot:[`item.invoiceAccountant`]="{ item: purchase }">
                <div
                    v-if="
                        getDisplayedInvoices(purchase) &&
                            getDisplayedInvoices(purchase).length > 0
                    "
                >
                    <div
                        v-for="(invoice, index) in getDisplayedInvoices(
                            purchase
                        )"
                        :key="index"
                        class="invoice-item-row d-flex align-center justify-center"
                    >
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <div
                                    @click.stop="
                                        onInvoiceCheckboxClick(
                                            invoice,
                                            purchase
                                        )
                                    "
                                    :style="{
                                        cursor: invoice.rejected
                                            ? 'default'
                                            : 'pointer',
                                    }"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-checkbox
                                        :value="
                                            getInvoiceAccountantStatus(
                                                invoice,
                                                purchase
                                            )
                                        "
                                        :input-value="
                                            getInvoiceAccountantStatus(
                                                invoice,
                                                purchase
                                            )
                                        "
                                        disabled
                                        dense
                                        hide-details
                                        class="my-0"
                                        :color="
                                            invoice.rejected
                                                ? 'grey'
                                                : invoiceMarked(invoice)
                                                ? 'error'
                                                : 'primary'
                                        "
                                    />
                                </div>
                            </template>
                            <span>{{
                                invoice.rejected
                                    ? 'Rejected invoice'
                                    : 'Click to relate invoice'
                            }}</span>
                        </v-tooltip>
                    </div>
                </div>
                <div v-else>
                    <p class="my-0">-</p>
                </div>
            </template>
            <template v-slot:[`item.totalPrice`]="{ item: purchase }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ formatNumber(purchase.totalRequestPrice) }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item: purchase }">
                <p class="my-0 text-capitalize">{{ purchase.project.name }}</p>
            </template>
            <template v-slot:[`item.actions`]="{ item: purchase }">
                <div
                    v-if="closedPurchasesPermission"
                    class="d-flex align-center justify-center"
                >
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <v-btn
                                    icon
                                    color="primary"
                                    :disabled="!canClosePurchase(purchase)"
                                    @click="openClosePurchaseDialog(purchase)"
                                    :loading="purchase.closeLoading"
                                >
                                    <v-icon>mdi-archive</v-icon>
                                </v-btn>
                            </span>
                        </template>
                        <span>{{
                            canClosePurchase(purchase)
                                ? 'Close Purchase'
                                : getClosePurchaseBlockReason(purchase)
                        }}</span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:[`item.notes`]="{ item: purchase }">
                <div class="d-flex align-center justify-center">
                    <v-btn
                        icon
                        small
                        color="primary"
                        @click="openNotesModal(purchase)"
                    >
                        <v-icon small>mdi-note-text</v-icon>
                    </v-btn>
                    <span class="ml-1" style="font-size: 12px;">
                        {{ purchase.notesCount || 0 }}
                    </span>
                </div>
            </template>
        </v-data-table>
        <!--ALERT MESSAGES-->
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--Request Form-->
        <v-dialog persistent fullscreen v-model="openForm">
            <PurchaseRequest
                v-if="openForm"
                :localRequest="selectedRequest"
                :originalRequest="originalRequest"
                :createForm="createForm"
                @closeDialog="closeRequestForm"
                @replaceRequest="replaceRequest"
                @closePurchase="closePurchaseForm"
            />
        </v-dialog>

        <!--Upload Invoices Modal-->
        <v-dialog
            v-model="uploadDialog"
            :max-width="selectedPurchaseForUpload ? '1000px' : '600px'"
            persistent
        >
            <v-card>
                <v-card-title class="headline">
                    <span>Upload Invoices</span>
                </v-card-title>

                <v-card-text>
                    <!-- Select Purchase -->
                    <v-combobox
                        v-model="selectedPurchaseForUpload"
                        :items="availableItems"
                        item-text="displayText"
                        item-value="id"
                        return-object
                        label="Select Purchase"
                        class="mb-4"
                        full-width
                        outlined
                        hide-details
                        menu-props="{
                            maxHeight: '300px',
                            'min-width': '100%'
                        }"
                        :item-height="48"
                    />

                    <!-- Select Order Number -->
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="selectedOrderNumber"
                                :items="
                                    selectedPurchaseForUpload
                                        ? selectedPurchaseForUpload.orderNumbers ||
                                          []
                                        : []
                                "
                                label="Select Order Number *"
                                outlined
                                dense
                                required
                                :disabled="
                                    !selectedPurchaseForUpload ||
                                        !selectedPurchaseForUpload.orderNumbers ||
                                        selectedPurchaseForUpload.orderNumbers
                                            .length === 0
                                "
                                :rules="[
                                    v => !!v || 'Order Number is required',
                                ]"
                            />
                        </v-col>
                    </v-row>

                    <!-- Invoice Information Section -->
                    <v-divider class="my-4"></v-divider>
                    <h4 class="text-h6 font-weight-bold mb-3">
                        Invoice Information
                    </h4>

                    <!-- Consecutive -->
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="selectedConsecutive"
                                label="Consecutive"
                                outlined
                                dense
                                hide-details
                            />
                        </v-col>
                    </v-row>
                    <v-alert
                        v-if="consecutiveAlreadyAssociated"
                        type="error"
                        dense
                        text
                        class="mb-0"
                    >
                        The consecutive is already associated with an invoice
                    </v-alert>

                    <!-- Supplier | Total Price -->
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="selectedSupplier"
                                label="Supplier"
                                outlined
                                dense
                                hide-details
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model.number="selectedTotalPrice"
                                label="Total Price"
                                type="number"
                                outlined
                                dense
                                hide-details
                            />
                        </v-col>
                    </v-row>

                    <!-- Invoice Date | Due Date -->
                    <v-row>
                        <v-col cols="6">
                            <v-menu
                                ref="invoiceDateMenu"
                                v-model="invoiceDateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="selectedInvoiceDate"
                                        label="Invoice Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        placeholder="Select invoice date"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </template>
                                <v-date-picker
                                    v-model="selectedInvoiceDate"
                                    scrollable
                                    @input="invoiceDateMenu = false"
                                >
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="6">
                            <v-menu
                                ref="dueDateMenu"
                                v-model="dueDateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="selectedDueDate"
                                        label="Due Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        placeholder="Select due date"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </template>
                                <v-date-picker
                                    v-model="selectedDueDate"
                                    scrollable
                                    @input="dueDateMenu = false"
                                >
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>

                    <!-- Tabla de purchase seleccionada -->
                    <div v-if="selectedPurchaseForUpload">
                        <h4>Selected Purchase:</h4>
                        <v-data-table
                            :items="[selectedPurchaseForUpload]"
                            :headers="purchaseHeaders"
                            :loading="uploadLoading"
                            disable-pagination
                            hide-default-footer
                            dense
                        >
                            <template v-slot:[`item.requestNumber`]="{ item }">
                                <p
                                    class="my-0 text-center"
                                    style="font-size: 12px; font-weight: bold;"
                                >
                                    {{ item.requestNumber }}
                                </p>
                            </template>
                            <template v-slot:[`item.project.name`]="{ item }">
                                <p class="my-0" style="font-size: 12px;">
                                    {{ item.project.name }}
                                </p>
                            </template>
                            <template v-slot:[`item.items`]="{ item }">
                                <p
                                    class="my-0 text-center"
                                    style="font-size: 12px;"
                                >
                                    {{ item.items ? item.items.length : 0 }}
                                    items
                                </p>
                            </template>
                            <template v-slot:[`item.status`]="{ item }">
                                <p
                                    class="my-0 text-center"
                                    style="font-size: 12px;"
                                >
                                    {{ item.status || 'N/A' }}
                                </p>
                            </template>
                        </v-data-table>
                    </div>
                </v-card-text>

                <v-card-actions class="px-6">
                    <!-- Input oculto para archivos -->
                    <input
                        ref="fileInput"
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        @change="handleFileSelection"
                        style="display: none"
                    />
                    <!-- Botón para seleccionar archivos y lista de archivos -->
                    <div class="d-flex align-center mr-3">
                        <span class="text-caption mr-1 font-weight-bold"
                            >ATTACH:</span
                        >

                        <!-- Lista de archivos seleccionados -->
                        <div
                            v-if="selectedFiles.length > 0"
                            class="d-flex align-center mr-2 files-scroll-container"
                            @wheel="handleWheelScroll"
                        >
                            <div
                                v-for="(file, index) in selectedFiles"
                                :key="index"
                                class="d-flex align-center mr-1 file-item"
                            >
                                <span
                                    class="text-caption mr-1"
                                    style="font-size: 13px;"
                                    >{{ file.name }}</span
                                >
                                <v-btn
                                    icon
                                    x-small
                                    color="error"
                                    @click="removeFile(index)"
                                >
                                    <v-icon x-small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <!-- Botón para agregar más archivos -->
                        <v-btn
                            @click="$refs.fileInput.click()"
                            color="primary"
                            fab
                            x-small
                            :disabled="!selectedPurchaseForUpload"
                            elevation="0"
                        >
                            <v-icon x-small>mdi-plus</v-icon>
                        </v-btn>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        text
                        @click="closeUploadDialog"
                        :disabled="uploadLoading"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        small
                        rounded
                        color="primary"
                        @click="uploadInvoicesToServer"
                        :loading="uploadLoading"
                        :disabled="!canUpload || consecutiveAlreadyAssociated"
                    >
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!--Notes Modal-->
        <v-dialog v-model="notesDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title class="headline">
                    <span>Notes - {{ selectedItem?.description }}</span>
                </v-card-title>

                <v-card-text>
                    <!-- Display existing notes -->
                    <div v-if="selectedItemNotes.length > 0" class="mb-4">
                        <h4>Existing Notes:</h4>
                        <v-card
                            v-for="(note, index) in selectedItemNotes"
                            :key="index"
                            class="mb-2 pa-3"
                            outlined
                        >
                            <div
                                class="d-flex justify-space-between align-start"
                            >
                                <div class="flex-grow-1">
                                    <div class="mb-1" v-html="note.note"></div>
                                    <small class="text--secondary">
                                        By: {{ note.username }} on
                                        {{ formatNoteDate(note.date) }}
                                    </small>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- Add new note -->
                    <div>
                        <h4>Add New Note:</h4>
                        <vue-editor
                            :editor-options="editorOptions"
                            v-model="newNote"
                            :disabled="addingNote"
                        />
                    </div>
                </v-card-text>

                <v-card-actions class="px-6">
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        text
                        @click="closeNotesModal"
                        :disabled="addingNote"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        small
                        rounded
                        color="primary"
                        @click="addNote"
                        :loading="addingNote"
                        :disabled="isNoteEmpty || addingNote"
                    >
                        Add Note
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Modal para relacionar invoice con entries -->
        <RelateInvoiceToEntries
            :dialog="invoiceRelationDialog"
            :invoice="selectedInvoice"
            :purchase="selectedPurchase"
            :companyId="companyId"
            :checkWithPoAccountingPermission="checkWithPoAccountingPermission"
            @close="closeInvoiceRelationModal"
            @relate="handleInvoiceRelated"
            @openPurchase="openRequestFromPurchaseModal"
            @openInvoice="openInvoiceFromModal"
            @error="handleRelateError"
        />

        <!-- Edit Invoice Dialog -->
        <v-dialog
            v-model="editInvoiceDialog"
            max-width="800px"
            persistent
            :retain-focus="false"
        >
            <EditInvoice
                v-if="editInvoiceDialog"
                :invoice="selectedInvoice"
                :purchase="invoicePurchase"
                :can-edit-invoice="editPurchasingInvoicePermission"
                :can-reject-invoice="rejectAccountingInvoicePermission"
                :can-mark-credit-note="markInvoiceAsCreditNotePermission"
                @closeDialog="closeEditInvoiceDialog"
            />
        </v-dialog>

        <!-- Close Purchase Confirmation Dialog -->
        <v-dialog v-model="closePurchaseDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="headline">
                    <span>Close Purchase</span>
                </v-card-title>

                <v-card-text>
                    <p>
                        Are you sure you want to close purchase
                        <strong>{{ purchaseToClose?.requestNumber }}</strong
                        >?
                    </p>
                </v-card-text>

                <v-card-actions class="px-6">
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        text
                        @click="closeClosePurchaseDialog"
                        :disabled="purchaseToClose?.closeLoading"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        small
                        rounded
                        color="primary"
                        @click="confirmClosePurchase"
                        :loading="purchaseToClose?.closeLoading"
                        :disabled="purchaseToClose?.closeLoading"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="deleteInvoiceDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="headline">
                    <span>Delete Invoice</span>
                </v-card-title>

                <v-card-text>
                    <p>
                        Are you sure you want to delete invoice
                        <strong>{{
                            invoiceToDelete?.consecutive ||
                                invoiceToDelete?.fileName
                        }}</strong
                        >?
                    </p>
                </v-card-text>

                <v-card-actions class="px-6">
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        text
                        @click="closeDeleteInvoiceDialog"
                        :disabled="deletingInvoice"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        small
                        rounded
                        color="error"
                        @click="confirmDeleteInvoice"
                        :loading="deletingInvoice"
                        :disabled="deletingInvoice"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import PurchaseRequest from '@/components/Purchasing/PurchaseRequest.vue'
import EditInvoice from '@/components/accountant/EditInvoice.vue'
import RelateInvoiceToEntries from '@/components/accountant/RelateInvoiceToEntries.vue'
import { firestore, storage } from '@/services/firebase'
import moment from 'moment'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import API from '@/services/api'
import { openFile } from '@/services/storage/openFile.js'
import { VueEditor } from 'vue2-editor'
import {
    canClosePurchase as validateCanClosePurchase,
    getClosePurchaseBlockReason as getPurchaseCloseBlockReason,
} from '@/utils/purchaseCloseValidation.js'

export default {
    name: 'AccountantPurchasing',

    components: {
        PurchaseRequest,
        VueEditor,
        EditInvoice,
        RelateInvoiceToEntries,
    },
    data: () => ({
        createForm: false,
        selectedRequest: {},
        originalRequest: {},
        openForm: false,
        activateAlert: false,
        alertMessage: '',
        loading: false,
        tablePage: 1,
        itemsPerPage: 15,
        userSelectedPage: 1,
        suppressPageTracking: false,
        headers: [
            {
                text: 'ID',
                value: 'requestNumber',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'TYPE',
                value: 'type',
                sortable: false,
                align: 'left',
                width: '100px',
            },
            {
                text: 'PROJECT',
                value: 'project',
                sortable: false,
                align: 'left',
                width: '250px',
            },

            {
                text: 'RECEIVED / QTY',
                value: 'quantity',
                sortable: false,
                align: 'center',
                width: '130PX',
            },
            {
                text: 'ORDER',
                value: 'purchaseOrder.orderNumber',
                sortable: false,
                align: 'center',
            },
            {
                text: 'SUPPLIER',
                value: 'supplier',
                sortable: false,
                align: 'center',
            },
            {
                text: 'TOTAL PRICE',
                value: 'totalPrice',
                sortable: false,
                align: 'center',
                width: '108px',
            },
            {
                text: 'INVOICES',
                value: 'invoices',
                sortable: false,
                align: 'start',
                width: '180px',
            },
            {
                text: 'SAP',
                value: 'invoiceSap',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'ACCOUNTANT',
                value: 'invoiceAccountant',
                sortable: false,
                align: 'center',
                width: '130px',
            },
            {
                text: 'ISSUE DATE',
                value: 'invoiceIssueDate',
                sortable: false,
                align: 'center',
                width: '130px',
            },
            {
                text: 'DUE DATE',
                value: 'invoiceDueDate',
                sortable: false,
                align: 'center',
                width: '130px',
            },
            {
                text: 'NOTES',
                value: 'notes',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
                width: '150px',
            },
        ],
        requests: [],
        projectToFilter: undefined,
        itemsToFilter: undefined,
        orderToFilter: undefined,
        supplierToFilter: undefined,
        invoicesToFilter: undefined,
        issueDateToFilter: undefined,
        dueDateToFilter: undefined,
        typeToFilter: undefined,
        sapToFilter: null,
        accountantToFilter: null,
        // Draft filter fields (debounced -> *ToFilter)
        idToFilterDraft: '',
        projectToFilterDraft: '',
        itemsToFilterDraft: '',
        orderToFilterDraft: '',
        supplierToFilterDraft: '',
        invoicesToFilterDraft: '',
        issueDateToFilterDraft: '',
        dueDateToFilterDraft: '',
        typeToFilterDraft: '',
        filterSyncTimeoutId: null,
        filterSyncDelayMs: 200,

        companyId: JSON.parse(localStorage.getItem('company')),
        listener: null,
        idToFilter: undefined,
        resourceId: undefined,
        users: [],
        checkWithPoAccountingPermission: undefined,
        closedPurchasesPermission: false,
        editPurchasingInvoicePermission: undefined,
        rejectAccountingInvoicePermission: false,
        markInvoiceAsCreditNotePermission: false,
        deleteAccountingInvoicePermission: undefined,
        uploadLoading: false,
        uploadDialog: false,
        selectedFiles: [],
        selectedPurchaseForUpload: null,
        selectedOrderNumber: null,
        selectedSupplier: '',
        selectedTotalPrice: null,
        selectedInvoiceDate: null,
        selectedDueDate: null,
        selectedConsecutive: '',
        consecutiveAlreadyAssociated: false,
        invoiceDateMenu: false,
        dueDateMenu: false,
        editInvoiceDialog: false,

        invoiceRelationDialog: false,
        selectedInvoice: null,
        selectedPurchase: null,
        notesDialog: false,
        selectedItem: null,
        selectedItemNotes: [],
        newNote: '',
        addingNote: false,
        currentUser: null,
        closePurchaseDialog: false,
        purchaseToClose: null,
        deleteInvoiceDialog: false,
        invoiceToDelete: null,
        purchaseToDeleteInvoice: null,
        deletingInvoice: false,
        purchaseHeaders: [
            {
                text: 'ID',
                value: 'requestNumber',
                sortable: false,
                align: 'center',
            },
            {
                text: 'PROJECT',
                value: 'project.name',
                sortable: false,
            },
            {
                text: 'ITEMS',
                value: 'items',
                sortable: false,
                align: 'center',
            },
        ],
        editorOptions: {
            theme: 'snow',
            modules: {
                toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ list: 'bullet' }],
                    [{ list: 'ordered' }],
                    [{ color: [] }],
                    ['clean'],
                ],
            },
        },
    }),
    computed: {
        ...mapState(['notificationResource']),
        canOpenInvoiceForm() {
            return !!(
                this.editPurchasingInvoicePermission ||
                this.rejectAccountingInvoicePermission ||
                this.markInvoiceAsCreditNotePermission
            )
        },
        itemsForTable() {
            // Now working at purchase level
            let allPurchases = []
            this.requests.forEach(request => {
                if (request.items && request.items.length > 0) {
                    // Calculate aggregated purchase statistics
                    const totalItems = request.items.length
                    const totalQuantity = request.items.reduce(
                        (sum, item) => sum + (item.quantity || 0),
                        0
                    )
                    const totalReceived = request.items.reduce(
                        (sum, item) =>
                            sum + this.getTotalReceivedQuantity(item),
                        0
                    )

                    // Get all unique suppliers from items
                    const suppliers = new Set()
                    request.items.forEach(item => {
                        const supplierName = this.getSupplierName(item)
                        if (supplierName !== 'No supplier') {
                            suppliers.add(supplierName)
                        }
                    })

                    // Get all unique order numbers
                    const orderNumbers = new Set()
                    request.items.forEach(item => {
                        if (
                            item.purchaseOrder &&
                            item.purchaseOrder.orderNumber
                        ) {
                            orderNumbers.add(item.purchaseOrder.orderNumber)
                        }
                    })

                    // Hot-path precomputations (used by filter/sort on every keypress)
                    const createdOnSeconds =
                        request?.createdOn?.seconds ??
                        request?.createdOn?._seconds ??
                        0
                    const purchaseInvoices = this.normalizeInvoices(
                        request.invoices
                    )
                    const allDetailedInvoices = request.allDetailedInvoices
                    const hasInvoices =
                        (purchaseInvoices && purchaseInvoices.length > 0) ||
                        (allDetailedInvoices && allDetailedInvoices.length > 0)

                    allPurchases.push({
                        // Basic purchase information (available at purchase level)
                        id: request.id,
                        requestId: request.id,
                        requestNumber: request.number,
                        requestStatus: request.status,
                        project: request.project,
                        createdBy: request.createdBy,
                        projectManager: request.projectManager,
                        buyer: request.buyer,
                        createdOn: request.createdOn,
                        createdOnSeconds,
                        dueDate: request.dueDate,
                        importation: request.importation,
                        comexFinished: request.comexFinished,
                        type: request.type,
                        deliveryLocation: request.deliveryLocation,
                        sapPercentage: request.sapPercentage,
                        totalRequestPrice: this.calculateTotal(request),

                        // Purchase-specific fields - normalize invoices
                        purchaseInvoices,
                        allDetailedInvoices,
                        hasInvoices,
                        purchaseNotes: request.notes || '',

                        // Aggregated statistics
                        totalItems: totalItems,
                        totalQuantity: totalQuantity,
                        totalReceived: totalReceived,

                        // Consolidated item information
                        suppliers: Array.from(suppliers),
                        orderNumbers: Array.from(orderNumbers),

                        items: request.items,

                        // Calculated states
                        accountant: request.accountant || false,
                        accountantChecked: this.calculateAccountantChecked(
                            request
                        ),
                        accountantLoading: false,
                        sapChecked: false,
                        sapLoading: false,
                        closeLoading: false,

                        // Purchase-level notes (notesWithPo)
                        notesWithPo: request.notesWithPo || [],
                        notesCount: request.notesWithPo
                            ? request.notesWithPo.length
                            : 0,
                    })
                }
            })
            return allPurchases
        },
        filterRequests() {
            const conditions = []

            if (this.idToFilter) {
                conditions.push(this.filterForId)
            }

            if (this.projectToFilter) {
                conditions.push(this.filterForProject)
            }

            if (this.itemsToFilter) {
                conditions.push(this.filterForItems)
            }

            if (this.orderToFilter) {
                conditions.push(this.filterForOrder)
            }

            if (this.supplierToFilter) {
                conditions.push(this.filterForSupplier)
            }

            if (this.invoicesToFilter) {
                conditions.push(this.filterForInvoices)
            }

            if (this.issueDateToFilter) {
                conditions.push(this.filterForIssueDate)
            }

            if (this.dueDateToFilter) {
                conditions.push(this.filterForDueDate)
            }

            if (this.typeToFilter) {
                conditions.push(this.filterForType)
            }

            if (this.sapToFilter !== null && this.sapToFilter !== undefined) {
                conditions.push(this.filterForSap)
            }

            if (
                this.accountantToFilter !== null &&
                this.accountantToFilter !== undefined
            ) {
                conditions.push(this.filterForAccountant)
            }

            const basePurchases = Array.isArray(this.itemsForTable)
                ? this.itemsForTable
                : []
            const filteredPurchases =
                conditions.length > 0
                    ? basePurchases.filter(purchase =>
                          conditions.every(condition => condition(purchase))
                      )
                    : basePurchases

            // Sort purchases: first those with invoices
            return filteredPurchases.slice().sort((a, b) => {
                const aHasInvoices =
                    a.hasInvoices !== undefined
                        ? a.hasInvoices
                        : (a.purchaseInvoices &&
                              a.purchaseInvoices.length > 0) ||
                          (a.allDetailedInvoices &&
                              a.allDetailedInvoices.length > 0)
                const bHasInvoices =
                    b.hasInvoices !== undefined
                        ? b.hasInvoices
                        : (b.purchaseInvoices &&
                              b.purchaseInvoices.length > 0) ||
                          (b.allDetailedInvoices &&
                              b.allDetailedInvoices.length > 0)

                // If one has invoices and the other doesn't, the one with invoices goes first
                if (aHasInvoices && !bHasInvoices) {
                    return -1
                }
                if (!aHasInvoices && bHasInvoices) {
                    return 1
                }

                // If both have or don't have invoices, sort by creation date (most recent first)
                const aCreated =
                    a.createdOnSeconds ??
                    a?.createdOn?.seconds ??
                    a?.createdOn?._seconds ??
                    0
                const bCreated =
                    b.createdOnSeconds ??
                    b?.createdOn?.seconds ??
                    b?.createdOn?._seconds ??
                    0
                return bCreated - aCreated
            })
        },
        availableItems() {
            // Format purchases for combobox (include purchaseInvoices for upload name collision)
            return this.itemsForTable.map(purchase => ({
                id: purchase.id,
                requestNumber: purchase.requestNumber,
                project: purchase.project,
                orderNumbers: purchase.orderNumbers || [],
                displayText: `ID: ${purchase.requestNumber} / ORDERS: ${
                    purchase.orderNumbers && purchase.orderNumbers.length > 0
                        ? purchase.orderNumbers.join(', ')
                        : 'N/A'
                }`,
                items: purchase.items,
                purchaseInvoices: purchase.purchaseInvoices || [],
            }))
        },
        canUpload() {
            return (
                this.selectedPurchaseForUpload &&
                this.selectedFiles &&
                this.selectedFiles.length > 0 &&
                this.selectedOrderNumber !== null &&
                this.selectedOrderNumber !== undefined &&
                this.selectedOrderNumber !== ''
            )
        },
        isNoteEmpty() {
            if (!this.newNote) return true
            // Strip HTML tags and check if content is empty
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = this.newNote
            return tempDiv.textContent.trim().length === 0
        },
    },
    watch: {
        selectedConsecutive() {
            this.consecutiveAlreadyAssociated = false
        },
        selectedPurchaseForUpload() {
            this.consecutiveAlreadyAssociated = false
        },
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
        tablePage(page) {
            if (this.suppressPageTracking) return
            this.userSelectedPage = page
        },
        filterRequests() {
            this.suppressPageTracking = true
            const pageToKeep = this.userSelectedPage
            this.$nextTick(() => {
                this.restoreTablePage(pageToKeep)
                this.$nextTick(() => {
                    this.suppressPageTracking = false
                })
            })
        },
    },
    async created() {
        try {
            this.loading = true
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    async mounted() {
        try {
            this.resourceId = this.$route.query.resourceId
            const {
                data: { user },
            } = await API.getMyInfo()

            this.currentUser = user
            this.checkWithPoAccountingPermission = user.permissions.includes(
                'checkWithPoAccounting'
            )
            this.editPurchasingInvoicePermission = user.permissions.includes(
                'editPurchasingInvoice'
            )
            this.rejectAccountingInvoicePermission = user.permissions.includes(
                'rejectAccountingInvoice'
            )
            this.markInvoiceAsCreditNotePermission = user.permissions.includes(
                'markInvoiceAsCreditNote'
            )
            this.deleteAccountingInvoicePermission = user.permissions.includes(
                'deleteAccountingInvoice'
            )
            this.closedPurchasesPermission = user.permissions.includes(
                'closedPurchases'
            )

            this.loading = true
            let PurchasesQuery = firestore
                .collection('companies')
                .doc(this.companyId)
                .collection('purchases')
                .where('status', 'in', [
                    'inTransit',
                    'received',
                    'readyToClose',
                ])

            if (!user.permissions.includes('withPoAccounting')) {
                return null
            }
            this.listener = await PurchasesQuery.onSnapshot(
                async docSnapshot => {
                    docSnapshot.docChanges().forEach(async change => {
                        const request = Object.assign(change.doc.data(), {
                            id: change.doc.id,
                        })

                        if (request.items) {
                            request.items.forEach(item => {
                                request.itemsLabel = request.itemsLabel
                                    ? request.itemsLabel +
                                      `${item.description} ${
                                          item.purchaseOrder
                                              ? '(' +
                                                item.purchaseOrder.orderNumber +
                                                ')'
                                              : ''
                                      }`
                                    : `${item.description} ${
                                          item.purchaseOrder
                                              ? '(' +
                                                item.purchaseOrder.orderNumber +
                                                ')'
                                              : ''
                                      }`
                            })
                        }
                        request.sapPercentage = this.getSapPercentage(request)
                        if (change.type === 'added') {
                            request.dueDate = this.formatDate(
                                request.dueDate.seconds
                            )
                            if (request.items) {
                                request.items.forEach(item => {
                                    item.accountantChecked =
                                        item.accountant || false
                                    item.accountantLoading = false
                                    item.sapChecked = this.getSapValue(item)
                                    item.sapLoading = false
                                })
                            }
                            this.requests.push(request)
                        }
                        if (change.type === 'modified') {
                            request.dueDate = this.formatDate(
                                request.dueDate.seconds
                            )
                            const index = this.requests.findIndex(
                                r => r.id == request.id
                            )
                            if (index >= 0) {
                                const existingRequest = this.requests[index]
                                if (existingRequest.items) {
                                    request.items.forEach((item, itemIndex) => {
                                        const existingItem =
                                            existingRequest.items[itemIndex]
                                        if (
                                            existingItem &&
                                            existingItem.accountantChecked !==
                                                undefined
                                        ) {
                                            item.accountantChecked =
                                                existingItem.accountantChecked
                                            item.accountantLoading =
                                                existingItem.accountantLoading ||
                                                false
                                            item.sapChecked =
                                                existingItem.sapChecked
                                            item.sapLoading =
                                                existingItem.sapLoading || false
                                        } else {
                                            item.accountantChecked =
                                                item.accountant || false
                                            item.accountantLoading = false
                                            item.sapChecked = this.getSapValue(
                                                item
                                            )
                                            item.sapLoading = false
                                        }
                                    })
                                }
                                this.requests.splice(index, 1, request)
                            }
                        }
                        if (change.type === 'removed') {
                            const index = this.requests.findIndex(
                                r => r.id == request.id
                            )
                            if (index >= 0) {
                                this.requests.splice(index, 1)
                            }
                        }
                    })
                    // If we were asked to open a specific resource (route/notification),
                    // try again after snapshot updates populate `requests`.
                    if (this.resourceId) {
                        this.openResource()
                    }
                }
            )
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.listener) {
            this.listener()
            this.listener = null
        }
        if (this.filterSyncTimeoutId) {
            clearTimeout(this.filterSyncTimeoutId)
            this.filterSyncTimeoutId = null
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        scheduleFilterSync() {
            if (this.filterSyncTimeoutId) {
                clearTimeout(this.filterSyncTimeoutId)
            }
            this.filterSyncTimeoutId = setTimeout(() => {
                this.applyFilterDrafts()
            }, this.filterSyncDelayMs)
        },
        applyFilterDrafts() {
            const normalize = value => {
                if (value === null || value === undefined) return undefined
                const strValue = String(value).trim()
                return strValue === '' ? undefined : strValue
            }

            this.idToFilter = normalize(this.idToFilterDraft)
            this.typeToFilter = normalize(this.typeToFilterDraft)
            this.projectToFilter = normalize(this.projectToFilterDraft)
            this.itemsToFilter = normalize(this.itemsToFilterDraft)
            this.orderToFilter = normalize(this.orderToFilterDraft)
            this.supplierToFilter = normalize(this.supplierToFilterDraft)
            this.invoicesToFilter = normalize(this.invoicesToFilterDraft)
            this.issueDateToFilter = normalize(this.issueDateToFilterDraft)
            this.dueDateToFilter = normalize(this.dueDateToFilterDraft)
        },
        restoreTablePage(pageToKeep) {
            const total = Array.isArray(this.filterRequests)
                ? this.filterRequests.length
                : 0
            const perPage =
                this.itemsPerPage === -1
                    ? Math.max(total, 1)
                    : this.itemsPerPage
            const pageCount = Math.max(1, Math.ceil(total / perPage) || 1)
            const nextPage = Math.min(Math.max(1, pageToKeep), pageCount)
            if (this.tablePage !== nextPage) {
                this.tablePage = nextPage
            }
        },
        handleWheelScroll(event) {
            const container = event.currentTarget
            event.preventDefault()
            container.scrollLeft += event.deltaY
        },
        async openInvoice(invoice, item) {
            await openFile(
                invoice.fileName,
                `${this.companyId}/purchases/${item.requestId}/invoices`
            )
        },
        async openInvoiceFromModal(invoice) {
            await openFile(
                invoice.fileName,
                `${this.companyId}/purchases/${this.selectedPurchase.id}/invoices`
            )
        },
        formatNumber(number) {
            return new Intl.NumberFormat('de-DE').format(
                Math.ceil(number != undefined ? number : 0)
            )
        },
        filterForId(purchase) {
            return purchase.requestNumber.toString().includes(this.idToFilter)
        },
        filterForItems(purchase) {
            // Search in descriptions of all purchase items
            return purchase.items.some(
                item =>
                    item.description &&
                    item.description
                        .toLowerCase()
                        .includes(this.itemsToFilter.toLowerCase())
            )
        },
        formatDate(seconds) {
            return moment.unix(seconds).format('YYYY-MM-DD')
        },
        closePurchaseForm() {
            const closedId = this.selectedRequest?.id
            if (closedId) {
                const index = this.requests.findIndex(r => r.id === closedId)
                if (index >= 0) {
                    this.requests.splice(index, 1)
                }
            }
            this.alertMessage = 'Purchase was successfully Closed'
            this.activateAlert = true
            this.closeRequestForm()
        },
        filterForDeliveryAt(item) {
            return (
                item.deliveryAt &&
                item.deliveryAt
                    .toLowerCase()
                    .includes(this.deliveryParamToFilter.toLowerCase())
            )
        },
        filterForProject(purchase) {
            return (
                purchase.project &&
                purchase.project.name
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase())
            )
        },
        filterForOrder(purchase) {
            // Search in order numbers of all purchase items
            return purchase.orderNumbers.some(orderNumber =>
                orderNumber
                    .toLowerCase()
                    .includes(this.orderToFilter.toLowerCase())
            )
        },
        filterForDate(item) {
            const date = this.formatDate(
                item.createdOn.seconds
                    ? item.createdOn.seconds
                    : item.createdOn._seconds
            )
            return date.includes(this.dateToFilter)
        },
        filterForNotes(item) {
            return (
                item.notes &&
                item.notes
                    .toLowerCase()
                    .includes(this.notesToFilter.toLowerCase())
            )
        },
        filterForLocation(item) {
            return (
                item.deliveryLocation &&
                item.deliveryLocation
                    .toLowerCase()
                    .includes(this.locationToFilter.toLowerCase())
            )
        },
        filterForUsers(item) {
            return (
                (item.createdBy.name &&
                    item.createdBy.name
                        .toLowerCase()
                        .includes(this.usersToFilter.toLowerCase())) ||
                (item.projectManager.name &&
                    item.projectManager.name
                        .toLowerCase()
                        .includes(this.usersToFilter.toLowerCase())) ||
                (item.buyer &&
                this.users.find(user => user.id == item.buyer) != undefined
                    ? this.users
                          .find(user => user.id == item.buyer)
                          .name.toLowerCase()
                          .includes(this.usersToFilter.toLowerCase())
                    : false)
            )
        },
        filterForType(purchase) {
            return (
                purchase.type &&
                purchase.type
                    .toLowerCase()
                    .includes(this.typeToFilter.toLowerCase())
            )
        },
        filterForInvoices(purchase) {
            // Search in purchase invoices (array of objects)
            return purchase.purchaseInvoices.some(invoice => {
                // Search by fileName
                const fileNameMatch =
                    invoice.fileName &&
                    invoice.fileName
                        .toLowerCase()
                        .includes(this.invoicesToFilter.toLowerCase())

                // Search by consecutive
                const consecutiveMatch =
                    invoice.consecutive &&
                    invoice.consecutive
                        .toLowerCase()
                        .includes(this.invoicesToFilter.toLowerCase())

                // Search by orderNumber
                const orderNumberMatch =
                    invoice.orderNumber &&
                    invoice.orderNumber
                        .toLowerCase()
                        .includes(this.invoicesToFilter.toLowerCase())

                return fileNameMatch || consecutiveMatch || orderNumberMatch
            })
        },
        getFilteredInvoices(purchase) {
            if (
                !purchase.purchaseInvoices ||
                purchase.purchaseInvoices.length === 0
            ) {
                return []
            }

            let filteredInvoices = purchase.purchaseInvoices

            // Apply text filter if exists
            if (this.invoicesToFilter && this.invoicesToFilter.trim() !== '') {
                const filterText = this.invoicesToFilter.toLowerCase().trim()
                filteredInvoices = filteredInvoices.filter(invoice => {
                    const consecutiveMatch =
                        invoice.consecutive &&
                        invoice.consecutive.toLowerCase().includes(filterText)

                    const fileNameMatch =
                        invoice.fileName &&
                        invoice.fileName.toLowerCase().includes(filterText)

                    const orderNumberMatch =
                        invoice.orderNumber &&
                        invoice.orderNumber.toLowerCase().includes(filterText)

                    return consecutiveMatch || fileNameMatch || orderNumberMatch
                })
            }

            // Apply SAP filter if exists
            if (this.sapToFilter !== null && this.sapToFilter !== undefined) {
                filteredInvoices = filteredInvoices.filter(invoice => {
                    const sapStatus = this.getInvoiceSapStatus(
                        invoice,
                        purchase
                    )
                    return sapStatus === this.sapToFilter
                })
            }

            // Apply ACCOUNTANT filter if exists
            if (
                this.accountantToFilter !== null &&
                this.accountantToFilter !== undefined
            ) {
                filteredInvoices = filteredInvoices.filter(invoice => {
                    const accountantStatus = this.getInvoiceAccountantStatus(
                        invoice,
                        purchase
                    )
                    return accountantStatus === this.accountantToFilter
                })
            }

            return filteredInvoices
        },
        getDisplayedInvoices(purchase) {
            let invoices = this.getFilteredInvoices(purchase)
            if (invoices.length === 0) {
                return []
            }
            if (
                this.issueDateToFilter &&
                this.issueDateToFilter.trim() !== ''
            ) {
                const issueDateFilter = this.issueDateToFilter
                    .toLowerCase()
                    .trim()
                invoices = invoices.filter(invoice => {
                    if (!invoice.invoicesDate) {
                        return false
                    }
                    return invoice.invoicesDate
                        .toLowerCase()
                        .includes(issueDateFilter)
                })
            }
            if (this.dueDateToFilter && this.dueDateToFilter.trim() !== '') {
                const dueDateFilter = this.dueDateToFilter.toLowerCase().trim()
                invoices = invoices.filter(invoice => {
                    if (!invoice.dueDate) {
                        return false
                    }
                    return invoice.dueDate.toLowerCase().includes(dueDateFilter)
                })
            }
            return invoices
        },
        filterForIssueDate(purchase) {
            const invoices = this.getFilteredInvoices(purchase)
            if (invoices.length === 0) {
                return false
            }
            if (
                !this.issueDateToFilter ||
                this.issueDateToFilter.trim() === ''
            ) {
                return true
            }
            const issueDateFilter = this.issueDateToFilter.toLowerCase().trim()
            return invoices.some(invoice => {
                if (!invoice.invoicesDate) {
                    return false
                }
                return invoice.invoicesDate
                    .toLowerCase()
                    .includes(issueDateFilter)
            })
        },
        filterForDueDate(purchase) {
            const invoices = this.getFilteredInvoices(purchase)
            if (invoices.length === 0) {
                return false
            }
            if (!this.dueDateToFilter || this.dueDateToFilter.trim() === '') {
                return true
            }
            const dueDateFilter = this.dueDateToFilter.toLowerCase().trim()
            return invoices.some(invoice => {
                if (!invoice.dueDate) {
                    return false
                }
                return invoice.dueDate.toLowerCase().includes(dueDateFilter)
            })
        },
        filterForSap(purchase) {
            if (
                !purchase.purchaseInvoices ||
                purchase.purchaseInvoices.length === 0
            ) {
                return false
            }
            // Filter invoices that match the SAP status
            const matchingInvoices = purchase.purchaseInvoices.filter(
                invoice => {
                    const sapStatus = this.getInvoiceSapStatus(
                        invoice,
                        purchase
                    )
                    return sapStatus === this.sapToFilter
                }
            )
            // Only show purchase if it has at least one matching invoice
            return matchingInvoices.length > 0
        },
        filterForAccountant(purchase) {
            if (
                !purchase.purchaseInvoices ||
                purchase.purchaseInvoices.length === 0
            ) {
                return false
            }
            // Filter invoices that match the ACCOUNTANT status
            const matchingInvoices = purchase.purchaseInvoices.filter(
                invoice => {
                    const accountantStatus = this.getInvoiceAccountantStatus(
                        invoice,
                        purchase
                    )
                    return accountantStatus === this.accountantToFilter
                }
            )
            // Only show purchase if it has at least one matching invoice
            return matchingInvoices.length > 0
        },
        getSupplierName(item) {
            if (!item.quotes || item.quotes.length === 0) {
                return 'No supplier'
            }

            const enabledQuote = item.quotes.find(quote => quote.able === true)

            if (enabledQuote && enabledQuote.supplier) {
                return enabledQuote.supplier.name
            }

            const firstQuote = item.quotes[0]
            if (firstQuote && firstQuote.supplier) {
                return firstQuote.supplier.name
            }

            return 'No supplier'
        },

        getOrderNumber(item) {
            if (item.purchaseOrder && item.purchaseOrder.orderNumber) {
                return item.purchaseOrder.orderNumber
            }
            return 'No order number'
        },

        filterForSupplier(purchase) {
            // Search in suppliers of all purchase items
            return purchase.suppliers.some(supplierName =>
                supplierName
                    .toLowerCase()
                    .includes(this.supplierToFilter.toLowerCase())
            )
        },

        getUser(id) {
            const user = this.users.find(u => u.id == id)
            if (user) {
                return user.name
            }
            return null
        },
        openRequest(item) {
            const request = this.requests.find(r => r.id === item.requestId)
            if (request) {
                this.selectedRequest = { ...request }
                this.originalRequest = { ...request }
                this.openForm = true
            }
        },
        closeRequestForm() {
            this.openForm = false
            this.selectedRequest = {}
            this.originalRequest = {}
        },
        replaceRequest() {},
        openResource() {
            if (this.resourceId) {
                const resource = this.requests.find(
                    r => r.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    if (resource.items && resource.items.length > 0) {
                        const firstItem = {
                            ...resource.items[0],
                            requestId: resource.id,
                            requestNumber: resource.number,
                            requestStatus: resource.status,
                            project: resource.project,
                            createdBy: resource.createdBy,
                            projectManager: resource.projectManager,
                            buyer: resource.buyer,
                            accountant: resource.items[0].accountant,
                            accountantChecked:
                                resource.items[0].accountant || false,
                            accountantLoading: false,
                            createdOn: resource.createdOn,
                            dueDate: resource.dueDate,
                            importation: resource.importation,
                            type: resource.type,
                            notes: resource.notes,
                            deliveryLocation: resource.deliveryLocation,
                            totalRequestPrice: this.calculateTotal(resource),
                            sapPercentage: resource.sapPercentage,
                        }
                        this.openRequest(firstItem)
                    }
                }
            }
        },
        sort() {
            this.requests.sort(
                (a, b) => b.createdOn.seconds - a.createdOn.seconds
            )
        },
        calculateTotal(request) {
            let calculatedTotal = 0
            request.items = request.items ? request.items : []
            request.items.forEach(i => {
                i.quotes = i.quotes ? i.quotes : []
                i.quotes.forEach(q => {
                    if (q.able) {
                        calculatedTotal += i.quantity * q.price
                    }
                })
            })
            return calculatedTotal
        },
        calculateItemTotal(item) {
            let calculatedTotal = 0
            item.quotes = item.quotes ? item.quotes : []
            item.quotes.forEach(q => {
                if (q.able) {
                    calculatedTotal += item.quantity * q.price
                }
            })
            return calculatedTotal
        },
        getSapPercentage(purchase) {
            try {
                let entriesQty = 0
                let sapQty = 0
                if (purchase?.items?.length > 0) {
                    purchase.items.forEach(item => {
                        if (item.entries && item.entries.length > 0) {
                            item.entries.forEach(entry => {
                                entriesQty++
                                if (entry?.sap === true) {
                                    sapQty++
                                }
                            })
                        }
                    })
                }
                return (entriesQty > 0
                    ? (sapQty / entriesQty) * 100
                    : 0
                ).toFixed(2)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        getTotalReceivedQuantity(item) {
            if (!item.entries || item.entries.length === 0) {
                return 0
            }
            return item.entries.reduce((total, entry) => {
                return total + (entry.quantity || 0)
            }, 0)
        },
        getQuantityStatusColor(item) {
            const qty = item.quantity || 0
            const received = this.getTotalReceivedQuantity(item)

            if (received === 0) {
                return 'red' // Rojo: nada recibido
            } else if (received >= qty) {
                return 'green' // Verde: cantidad igual o mayor al qty
            } else {
                return 'orange' // Amarillo: cantidad menor al qty
            }
        },
        getPurchaseQuantityStatusColor(purchase) {
            const totalQty = purchase.totalQuantity || 0
            const totalReceived = purchase.totalReceived || 0

            if (totalReceived === 0) {
                return 'red' // Rojo: nada recibido
            } else if (totalReceived >= totalQty) {
                return 'green' // Verde: cantidad igual o mayor al qty
            } else {
                return 'orange' // Amarillo: cantidad menor al qty
            }
        },
        /** Firestore may store `invoices` as a JSON string instead of an array. */
        parseInvoicesArray(raw) {
            if (raw == null || raw === '') return []
            if (Array.isArray(raw)) return raw
            if (typeof raw === 'string') {
                try {
                    const parsed = JSON.parse(raw)
                    return Array.isArray(parsed) ? parsed : []
                } catch {
                    return []
                }
            }
            return []
        },
        normalizeInvoices(invoices) {
            const list = this.parseInvoicesArray(invoices)
            if (!list.length) return []

            return list
                .filter(invoice => {
                    // Filter undefined, null, or empty string values
                    if (!invoice || invoice === 'undefined' || invoice === '') {
                        return false
                    }
                    return true
                })
                .map(invoice => {
                    if (typeof invoice === 'object' && invoice !== null) {
                        const fileName = invoice.fileName || null
                        if (fileName) {
                            return {
                                fileName: fileName,
                                invoicesDate:
                                    invoice.invoiceDate || invoice.invoicesDate,
                                nit: invoice.nit || false,
                                legalName: invoice.legalName || false,
                                dueDate: invoice.dueDate || null,
                                consecutive: invoice.consecutive || null,
                                orderNumber: invoice.orderNumber || null,
                                sap: invoice.sap || false,
                                relatedEntries: invoice.relatedEntries || [],
                                rejected: invoice.rejected || false,
                                frozenChecks: invoice.frozenChecks || null,
                                creditNote: !!invoice.creditNote,
                                creditNotePreviousConsecutive:
                                    invoice.creditNotePreviousConsecutive ??
                                    null,
                                pastFiles: invoice.pastFiles || [],
                                invoiceAttachments:
                                    invoice.invoiceAttachments || [],
                            }
                        }
                    }

                    // Convert string to object if needed
                    if (typeof invoice === 'string') {
                        return {
                            fileName: invoice,
                            invoicesDate: '',
                            nit: true,
                            legalName: true,
                            dueDate: null,
                            sap: false,
                            relatedEntries: [],
                            rejected: false,
                            pastFiles: [],
                            invoiceAttachments: [],
                        }
                    }

                    return null
                })
                .filter(invoice => invoice !== null) // Remove null values
        },
        invoiceMarked(invoice) {
            return !!(invoice && (invoice.rejected || invoice.creditNote))
        },
        invoiceNameColor(invoice) {
            if (this.invoiceMarked(invoice)) return '#f44336'
            return this.getInvoiceColor(invoice)
        },
        getInvoiceColor(invoice) {
            const hasLegalName = invoice.legalName === true
            const hasNit = invoice.nit === true

            if (!hasLegalName && !hasNit) {
                return '#f44336' // Rojo: ambos campos son false
            } else if (hasLegalName && hasNit) {
                return '#2b81d6' // Azul: ambos campos son true
            } else {
                return '#ff9800' // Amarillo: solo uno de los dos es true
            }
        },
        getSapValue(item) {
            if (!item.entries || item.entries.length === 0) {
                return false
            }

            return item.entries.some(entry => entry.sap === true)
        },
        async updateSapStatus(purchase) {
            try {
                this.$set(purchase, 'sapLoading', true)

                // Verify that all items have entries
                const itemsWithoutEntries = purchase.items.filter(
                    item => !item.entries || item.entries.length === 0
                )

                if (itemsWithoutEntries.length > 0) {
                    this.$nextTick(() => {
                        purchase.sapChecked = false
                    })

                    // Disable loading state
                    this.$set(purchase, 'sapLoading', false)

                    this.setErrorItems({
                        source: this.$options.name,
                        message: `Cannot update SAP for purchase "${purchase.requestNumber}" - Some items have no received quantities. Please ensure all items have been received first.`,
                    })
                    return
                }

                // Update SAP for all purchase items
                for (const item of purchase.items) {
                    for (const entry of item.entries) {
                        await API.updateEntryToRequestsItem({
                            requestId: purchase.id,
                            itemId: item.id,
                            entryId: entry.id,
                            sap: purchase.sapChecked,
                        })
                    }
                }

                this.alertMessage = `SAP status updated successfully for purchase ${purchase.requestNumber}`
                this.activateAlert = true
            } catch (error) {
                // Revert state in case of error
                purchase.sapChecked = !purchase.sapChecked

                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error updating SAP status: ${error.message}`,
                })
            } finally {
                this.$set(purchase, 'sapLoading', false)
            }
        },
        async uploadInvoices() {
            try {
                this.uploadLoading = true
                this.uploadDialog = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error uploading invoices: ${error.message}`,
                })
            } finally {
                this.uploadLoading = false
            }
        },
        /** Collects invoice PDF names already used on the purchase (current + history). */
        collectUsedInvoiceFileNamesFromPurchase(purchase) {
            const names = new Set()
            const addInvoiceEntry = inv => {
                if (inv == null) return
                if (typeof inv === 'string') {
                    if (inv) names.add(inv)
                    return
                }
                const fn = inv.fileName || inv.filename || inv.nameFile
                if (fn) names.add(fn)
                if (Array.isArray(inv.pastFiles)) {
                    inv.pastFiles.forEach(f => names.add(f))
                }
            }

            const normalized = purchase?.purchaseInvoices || []
            const fromRaw = this.parseInvoicesArray(purchase?.invoices)
            ;[...normalized, ...fromRaw].forEach(addInvoiceEntry)

            if (Array.isArray(purchase?.allDetailedInvoices)) {
                purchase.allDetailedInvoices.forEach(addInvoiceEntry)
            }
            return names
        },
        /** Combobox selection may omit nested fields; table snapshot is canonical. */
        getPurchaseSnapshotForUpload() {
            const sel = this.selectedPurchaseForUpload
            if (!sel) return null
            const id = typeof sel === 'object' && sel !== null ? sel.id : null
            if (id) {
                const fromTable = this.itemsForTable.find(p => p.id === id)
                if (fromTable) return fromTable
            }
            return typeof sel === 'object' ? sel : null
        },
        handleFileSelection(event) {
            const newFiles = Array.from(event.target.files)
            const purchase = this.getPurchaseSnapshotForUpload()
            const existingNames = purchase
                ? this.collectUsedInvoiceFileNamesFromPurchase(purchase)
                : new Set()
            const usedNames = new Set([
                ...existingNames,
                ...this.selectedFiles.map(f => f.name),
            ])

            const renamedFiles = newFiles.map(file => {
                const newFileName = this.uniqueFileName(file.name, usedNames)
                return new File([file], newFileName, {
                    type: file.type,
                    lastModified: file.lastModified,
                })
            })
            this.selectedFiles = [...this.selectedFiles, ...renamedFiles]
            event.target.value = ''
        },
        /**
         * Storage-safe unique names in the same folder; registers in `usedNames`.
         */
        uniqueFileName(originalName, usedNames) {
            const lastDot = originalName.lastIndexOf('.')
            const extension =
                lastDot !== -1 ? originalName.substring(lastDot) : ''
            const base =
                lastDot !== -1
                    ? originalName.substring(0, lastDot)
                    : originalName

            for (let n = 0; n < 10000; n += 1) {
                const candidate =
                    n === 0
                        ? `${base}${extension}`
                        : `${base} (${n})${extension}`
                if (!usedNames.has(candidate)) {
                    usedNames.add(candidate)
                    return candidate
                }
            }
            const fallback = `${base} (${Date.now()})${extension}`
            usedNames.add(fallback)
            return fallback
        },
        removeFile(index) {
            this.selectedFiles.splice(index, 1)
        },
        closeUploadDialog() {
            this.uploadDialog = false
            this.selectedFiles = []
            this.selectedPurchaseForUpload = null
            this.selectedOrderNumber = null
            this.selectedSupplier = ''
            this.selectedTotalPrice = null
            this.selectedInvoiceDate = null
            this.selectedDueDate = null
            this.selectedConsecutive = ''
            this.consecutiveAlreadyAssociated = false
            this.$refs.fileInput.value = ''
        },
        purchaseHasConsecutive(purchase, consecutive) {
            const target = String(consecutive || '')
                .trim()
                .toLowerCase()
            if (!target || !purchase) return false

            const invoices = [
                ...(purchase.purchaseInvoices || []),
                ...this.parseInvoicesArray(purchase.invoices),
            ]
            return invoices.some(invoice => {
                if (!invoice || typeof invoice !== 'object') return false
                return (
                    String(invoice.consecutive || '')
                        .trim()
                        .toLowerCase() === target
                )
            })
        },
        async uploadInvoicesToServer() {
            try {
                this.uploadLoading = true
                const uploadedFileNames = []

                if (!this.selectedPurchaseForUpload) {
                    throw new Error('No purchase selected')
                }

                const consecutive = (this.selectedConsecutive || '').trim()
                const purchase = this.getPurchaseSnapshotForUpload()
                if (
                    consecutive &&
                    this.purchaseHasConsecutive(purchase, consecutive)
                ) {
                    this.consecutiveAlreadyAssociated = true
                    return
                }
                this.consecutiveAlreadyAssociated = false

                // Upload files to Firebase Storage
                for (const file of this.selectedFiles) {
                    const path = `${this.companyId}/purchases/${this.selectedPurchaseForUpload.id}/invoices`
                    const storageRef = storage().ref(path)
                    const fileRef = storageRef.child(file.name)

                    const metadata = {
                        customMetadata: {
                            filename: file.name,
                        },
                    }

                    await fileRef.put(file, metadata)

                    // Add filename if not already in the list
                    if (!uploadedFileNames.includes(file.name)) {
                        uploadedFileNames.push(file.name)
                    }
                }

                // Convert filenames to invoice objects with orderNumber (required)
                const invoicesToUpload = uploadedFileNames.map(fileName => {
                    const invoice = {
                        fileName: fileName,
                        orderNumber: this.selectedOrderNumber,
                        invoicesDate: moment().format('YYYY-MM-DD'),
                        nit: true,
                        legalName: true,
                        dueDate: this.selectedDueDate || null,
                        invoiceDate: this.selectedInvoiceDate,
                        sap: false,
                        relatedEntries: [],
                        rejected: false,
                        pastFiles: [],
                        invoiceAttachments: [],
                        supplier: this.selectedSupplier.trim() || '',
                        totalPrice: Number(this.selectedTotalPrice) || null,
                        consecutive: this.selectedConsecutive.trim() || null,
                    }
                    return invoice
                })

                // Update invoices array of selected purchase
                if (invoicesToUpload.length > 0) {
                    await API.updatePurchaseInvoices({
                        companyId: this.companyId,
                        purchaseId: this.selectedPurchaseForUpload.id,
                        invoices: invoicesToUpload,
                    })
                }

                this.alertMessage = 'Invoices uploaded successfully'
                this.activateAlert = true
                this.closeUploadDialog()
            } catch (error) {
                if (
                    error.message &&
                    error.message.includes('already associated with an invoice')
                ) {
                    this.consecutiveAlreadyAssociated = true
                    return
                }
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error uploading invoices: ${error.message}`,
                })
            } finally {
                this.uploadLoading = false
            }
        },

        onInvoiceCheckboxClick(invoice, purchase) {
            if (invoice.rejected) {
                return
            }
            this.openInvoiceRelationModal(invoice, purchase)
        },
        openInvoiceRelationModal(invoice, purchase) {
            // Prevent opening modal multiple times
            if (this.invoiceRelationDialog) {
                return
            }

            this.selectedInvoice = invoice
            this.selectedPurchase = purchase
            this.invoiceRelationDialog = true
        },

        closeInvoiceRelationModal() {
            this.invoiceRelationDialog = false
            this.selectedInvoice = null
            this.selectedPurchase = null
        },
        openRequestFromPurchaseModal() {
            if (this.selectedPurchase) {
                const purchaseToOpen = { ...this.selectedPurchase }

                this.$nextTick(() => {
                    this.openRequest(purchaseToOpen)
                })
            }
        },
        handleInvoiceRelated({ purchase }) {
            // Merge updates into the existing Firestore-shaped request
            const requestIndex = this.requests.findIndex(
                r => r.id === purchase.id
            )
            if (requestIndex >= 0) {
                const existing = this.requests[requestIndex]
                this.$set(this.requests, requestIndex, {
                    ...existing,
                    items: purchase.items || existing.items,
                    invoices:
                        purchase.purchaseInvoices ||
                        purchase.invoices ||
                        existing.invoices,
                })
            }

            this.alertMessage = 'Invoice related to entries successfully'
            this.activateAlert = true
            this.closeInvoiceRelationModal()
        },
        handleRelateError({ message }) {
            this.setErrorItems({
                source: this.$options.name,
                message: message,
            })
        },

        async updateInvoiceSapStatus(invoice, purchase) {
            try {
                await this.updatePurchaseInvoiceSapStatus(invoice, purchase)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error updating SAP status: ${error.message}`,
                })
            }
        },

        async updatePurchaseInvoiceSapStatus(invoice, purchase) {
            try {
                // Update SAP status in purchase
                await API.updatePurchaseInvoices({
                    companyId: this.companyId,
                    purchaseId: purchase.id,
                    invoices: purchase.purchaseInvoices.map(inv =>
                        inv.fileName === invoice.fileName
                            ? { ...inv, sap: invoice.sap }
                            : inv
                    ),
                })
            } catch (error) {
                console.error(
                    'Error updating purchase invoice SAP status:',
                    error
                )
                throw error
            }
        },
        canClosePurchase(purchase) {
            return validateCanClosePurchase(purchase)
        },
        getClosePurchaseBlockReason(purchase) {
            return getPurchaseCloseBlockReason(purchase)
        },
        /**
         * Opens the close purchase confirmation dialog
         * @param {Object} purchase - Purchase to close
         */
        openClosePurchaseDialog(purchase) {
            this.purchaseToClose = purchase
            this.closePurchaseDialog = true
        },

        /**
         * Closes the close purchase confirmation dialog
         */
        closeClosePurchaseDialog() {
            this.closePurchaseDialog = false
            this.purchaseToClose = null
        },

        /**
         * Confirms and executes the purchase closing process
         */
        async confirmClosePurchase() {
            const purchase = this.purchaseToClose
            if (!purchase) return

            try {
                this.$set(purchase, 'closeLoading', true)

                if (!this.canClosePurchase(purchase)) {
                    throw new Error(
                        this.getClosePurchaseBlockReason(purchase) ||
                            'Cannot close this purchase yet.'
                    )
                }

                // Check if it's an importation and COMEX status
                const isImportation = purchase.importation === true
                const isComexFinished = purchase.comexFinished === true

                // Prepare parameters for closing
                const closeParams = {
                    id: purchase.id,
                    notificationId: 'pFDh25EcR74EMiuypq8M',
                }

                // Choose the correct endpoint based on purchase type
                if (isImportation && !isComexFinished) {
                    // COMEX: Use updatePurchaseRequest
                    await API.updatePurchaseRequest(purchase.id, {
                        status: 'closed',
                        ...closeParams,
                    })
                } else {
                    // NORMAL: Use closePurchaseRequest
                    await API.closePurchaseRequest(closeParams)
                }

                this.alertMessage = `Purchase ${purchase.requestNumber} closed successfully!`
                this.activateAlert = true
                this.closeClosePurchaseDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error closing purchase: ${error.message}`,
                })
            } finally {
                this.$set(purchase, 'closeLoading', false)
            }
        },
        openNotesModal(purchase) {
            this.selectedItem = purchase
            this.selectedItemNotes = purchase.notesWithPo || []
            this.newNote = ''
            this.notesDialog = true
        },
        closeNotesModal() {
            this.notesDialog = false
            this.selectedItem = null
            this.selectedItemNotes = []
            this.newNote = ''
        },
        async addNote() {
            if (!this.newNote.trim()) return

            try {
                this.addingNote = true

                const noteData = {
                    note: this.newNote.trim(),
                    date: new Date().toISOString(),
                    userId: this.currentUser?.id || 'unknown',
                    username: this.currentUser?.name || 'Unknown User',
                }

                await API.addPurchaseNote({
                    companyId: this.companyId,
                    requestId: this.selectedItem.id,
                    note: noteData,
                })

                this.selectedItemNotes.push(noteData)
                this.selectedItem.notesWithPo = this.selectedItemNotes
                this.selectedItem.notesCount = this.selectedItemNotes.length

                // Update the purchase in the requests array
                const requestIndex = this.requests.findIndex(
                    r => r.id === this.selectedItem.id
                )
                if (requestIndex >= 0) {
                    this.requests[
                        requestIndex
                    ].notesWithPo = this.selectedItemNotes
                }

                this.newNote = ''

                this.alertMessage = 'Note added successfully'
                this.activateAlert = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error adding note: ${error.message}`,
                })
            } finally {
                this.addingNote = false
            }
        },
        formatNoteDate(dateString) {
            return moment(dateString).format('YYYY-MM-DD HH:mm')
        },
        hasEntries(item) {
            return item.entries && item.entries.length > 0
        },
        hasRelatedEntries(invoice) {
            return invoice.relatedEntries && invoice.relatedEntries.length > 0
        },
        getInvoiceSapStatus(invoice, purchase) {
            if (invoice.rejected && invoice.frozenChecks) {
                return !!invoice.frozenChecks.sap
            }
            if (!this.hasRelatedEntries(invoice)) {
                return false
            }

            // Verify all related entries are marked as SAP
            return invoice.relatedEntries.every(relatedEntry => {
                // Find the entry in all items
                let foundEntry = null
                purchase.items.forEach(item => {
                    if (item.entries && item.entries.length > 0) {
                        const entry = item.entries.find(
                            e => e.id === relatedEntry.entryId
                        )
                        if (entry) {
                            foundEntry = entry
                        }
                    }
                })

                return foundEntry && foundEntry.sap === true
            })
        },
        getInvoiceAccountantStatus(invoice, purchase) {
            if (invoice.rejected && invoice.frozenChecks) {
                return !!invoice.frozenChecks.accountant
            }
            if (!this.hasRelatedEntries(invoice)) {
                return false
            }

            // Verify all related entries are marked as ACCOUNTANT
            return invoice.relatedEntries.every(relatedEntry => {
                // Find the entry in all items
                let foundEntry = null
                purchase.items.forEach(item => {
                    if (item.entries && item.entries.length > 0) {
                        const entry = item.entries.find(
                            e => e.id === relatedEntry.entryId
                        )
                        if (entry) {
                            foundEntry = entry
                        }
                    }
                })

                return foundEntry && foundEntry.accountant === true
            })
        },
        allInvoicesHaveSap(purchase) {
            // If there are no invoices, cannot mark accountant
            if (
                !purchase.purchaseInvoices ||
                purchase.purchaseInvoices.length === 0
            ) {
                return false
            }

            // Verify that all invoices have SAP = true
            return purchase.purchaseInvoices.every(invoice =>
                this.getInvoiceSapStatus(invoice, purchase)
            )
        },
        calculateAccountantChecked(request) {
            if (request.accountant === true) {
                return true
            }

            if (!request.items || request.items.length === 0) {
                return false
            }

            // Verify that all items have entries
            const itemsWithoutEntries = request.items.filter(
                item => !item.entries || item.entries.length === 0
            )
            if (itemsWithoutEntries.length > 0) {
                return false
            }

            // Verify that all entries have accountant = true
            return request.items.every(item => {
                if (!item.entries || item.entries.length === 0) {
                    return false
                }
                return item.entries.every(entry => entry.accountant === true)
            })
        },
        openEditInvoice(invoice, purchase) {
            this.selectedInvoice = invoice
            this.invoicePurchase = purchase
            this.editInvoiceDialog = true
        },
        deleteInvoice(invoice, purchase) {
            this.invoiceToDelete = invoice
            this.purchaseToDeleteInvoice = purchase
            this.deleteInvoiceDialog = true
        },
        closeDeleteInvoiceDialog() {
            this.deleteInvoiceDialog = false
            this.invoiceToDelete = null
            this.purchaseToDeleteInvoice = null
        },
        async confirmDeleteInvoice() {
            try {
                this.deletingInvoice = true
                await API.deletePurchaseInvoice({
                    companyId: this.companyId,
                    purchaseId: this.purchaseToDeleteInvoice.id,
                    fileName: this.invoiceToDelete.fileName,
                })
                this.closeDeleteInvoiceDialog()
                this.alertMessage = 'Invoice deleted successfully'
                this.activateAlert = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: `Error deleting invoice: ${error.message}`,
                })
            } finally {
                this.deletingInvoice = false
            }
        },
        closeEditInvoiceDialog() {
            this.editInvoiceDialog = false
            this.selectedInvoice = null
            this.invoicePurchase = null
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
    position: sticky;
    z-index: 1;
}
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 70vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 80px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}

/* Scroll horizontal para archivos adjuntos */
.files-scroll-container {
    max-width: 300px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-color: #ddd transparent;
}

.files-scroll-container::-webkit-scrollbar {
    height: 3px;
}

.files-scroll-container::-webkit-scrollbar-track {
    background: transparent;
}

.files-scroll-container::-webkit-scrollbar-thumb {
    background-color: #ddd;
    border-radius: 2px;
}

.files-scroll-container::-webkit-scrollbar-thumb:hover {
    background-color: #bbb;
}

.file-item {
    flex-shrink: 0;
    white-space: nowrap;
}

/* Invoice row alignment - ensures all invoice rows align across columns */
.invoice-item-row {
    display: flex;
    align-items: center;
    height: 40px;
    min-height: 40px;
    max-height: 40px;
    width: 100%;
    box-sizing: border-box;
}

/* Invoice filename container with fixed width */
.invoice-filename-container {
    flex-shrink: 0;
    overflow: hidden;
}

.invoice-filename {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.invoice-date {
    text-align: center;
    margin: 0;
    font-size: 11px;
    width: 100%;
}

/* Styles for date pickers */
.v-date-picker {
    background: white !important;
    border-radius: 4px;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
        0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
}

.v-date-picker-table {
    background: white !important;
}

.v-date-picker-table .v-btn {
    background: transparent !important;
}

.v-date-picker-table .v-btn--active {
    background: #1976d2 !important;
    color: white !important;
}

/* Ensure menu has solid background and correct width */
.v-menu__content {
    background: white !important;
    border-radius: 4px;
    overflow: hidden;
    min-width: 290px !important;
    max-width: 290px !important;
    width: 290px !important;
}

/* Control picker body width */
.v-picker__body {
    width: 290px !important;
    min-width: 290px !important;
    max-width: 290px !important;
}

/* Ensure picker container has correct width */
.v-picker {
    width: 290px !important;
    min-width: 290px !important;
    max-width: 290px !important;
}
</style>
